import { Injector } from "@angular/core";
import { Http } from "@angular/http";
import {
    ITCIApplication,
    ITCIApplicationUpdateProvider,
    ITCIApplicationUpdateRequest,
    ITCIApplicationUpdateResponse,
    ITCIValidationResult,
    SERVICE_URLS,
    TCI_UPDATE_ACTION_TYPE,
    USER_ROLES,
    TCITenantAppType
} from "@tci/common";
import { BaseTCIApplicationUpdateProvider } from "../tci-tenant-services";
import { NgUserService } from "../../ng-shared/ng-services/ng-user.service";
import { Observable, Observer, Subject } from "rxjs";

export class TciTenantBaseUpdateSharedService extends BaseTCIApplicationUpdateProvider implements ITCIApplicationUpdateProvider {
    private http: Http;
    private handlers: { [key: string]: boolean } = {};
    private status$: Subject<boolean>; // To track the update events
    private isDirty: { [key: string]: boolean };

    private _isWorking = false; // To avoid concurrent requests

    get isWorking(): boolean {
        return this._isWorking;
    }

    set isWorking(val: boolean) {
        this._isWorking = val;
        if (this.status$ && !this.status$.closed && !this.status$.isStopped) {
            this.status$.next(val);
        }
    }

    constructor(private userService: NgUserService, injector?: Injector, http?: Http) {
        super(injector, http);
        this.http = http;
    }

    /**
     * Validate the files selected by the user
     * The validation result can be an error or warning
     * @param input
     */
    validate(input: ITCIApplicationUpdateRequest): Observable<ITCIValidationResult> {
        return Observable.of(null);
    }

    /**
     * Before subscribing this method will be called to let the provider know
     * the consumer is subscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    startUpdateSubscription(app: ITCIApplication): Observable<boolean> {
        this.handlers[app.id] = true;
        this.isDirty = {};
        this.status$ = new Subject();
        return Observable.of(true);
    }

    /**
     * Before unsubscribing this method will be called to let the provider know
     * the consumer is unsubscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    stopUpdateSubscription(app: ITCIApplication): Observable<boolean> {
        if (!this.isWorking) {
            this.handlers[app.id] = false;
            delete this.isDirty;
            this.status$.unsubscribe();
        }
        return Observable.of(!this.isWorking);
    }

    /**
     * Disconnected Components that take actions related to a application name desc update
     * which impacts the Component state need to enclose the
     * actions within the callback so the update provider can let
     * other those dependencies get notified on the completion of the action
     * for performing refresh or any other action
     * @param input
     * @param callback
     */
    pushAppChanges(
        input: ITCIApplicationUpdateRequest,
        callback: (cinput: ITCIApplicationUpdateRequest) => Observable<ITCIApplicationUpdateResponse>
    ): Observable<ITCIApplicationUpdateResponse> {
        this.isWorking = true;
        return callback(input)
            .finally(() => this.isWorking = false);
        // return Observable.create((obs: Observer<ITCIApplicationUpdateResponse>) => {
        //     callback(input)
        //         .finally(() => this.isWorking = false)
        //         .subscribe(
        //             cbRes => obs.next(cbRes),
        //             error => obs.error(error)
        //         );
        // });
    }

    /**
     * Set the editing field state
     * @param isDirty True: Is dirty, False: Is pristine
     */
    setDirty(isDirty: boolean, type: TCI_UPDATE_ACTION_TYPE) {
        if (this.isDirty) { // Observer is started
            this.isDirty[type] = isDirty;
            this.status$.next(isDirty || this.isWorking);
        }
    }

    /**
     * Return state if the provider is busy waiting for the pushAppChanges to finish
     * @param input
     */
    isBusy(input: ITCIApplicationUpdateRequest): Observable<boolean> {
        if (this.handlers[input.appId]) {
            return Observable.create(observer =>
                this.status$.subscribe(res => observer.next(res))
            );
        } else {
            return Observable.of(false);
        }
    }

    canUpdateApplication(input: ITCIApplicationUpdateRequest): Observable<boolean> {
        if (this.isWorking) {
            return Observable.of(false);
        } else if (input.actionType === TCI_UPDATE_ACTION_TYPE.APP_NAME
            || input.actionType === TCI_UPDATE_ACTION_TYPE.APP_DESCRIPTION
            || input.actionType === TCI_UPDATE_ACTION_TYPE.APP_ENDPOINT) {
            return this.userService.getUserInfo()
                .map(token => !input.application.onPremise && (
                    USER_ROLES.ADM === token.rolX[0] ||
                    (USER_ROLES.USR === token.rolX[0] && input.application.owner === token.uname) ||
                    (USER_ROLES.RO !== token.rolX[0] && input.tenantType.equals(TCITenantAppType.scribe))
                ));
        } else if (input.actionType === TCI_UPDATE_ACTION_TYPE.UPDATE_TAGS) {
            return Observable.of(true);
        }
        return Observable.of(false);
    }

    protected getGenericResponse(input: ITCIApplicationUpdateRequest): ITCIApplicationUpdateResponse {
        return {
            appId: input.application.id,
            success: false,
            actionType: input.actionType,
            name: input.application.name
        };
    }

    updateApplication(input: ITCIApplicationUpdateRequest): Observable<ITCIApplicationUpdateResponse> {
        const output = this.getGenericResponse(input);

        if (input.actionType === TCI_UPDATE_ACTION_TYPE.APP_NAME) {
            if (!input.application.name) {
                output.msg = "This field is required";
            } else if (input.application.name.length > 70) {
                output.msg = "Up to 70 characters are allowed";
            } else if (!new RegExp(/^[a-zA-Z_][a-zA-Z0-9-_.]{0,69}$/).test(input.application.name)) {
                output.msg = "This field name must start with a letter or underscore and can include letters, digits, periods, dashes, and underscores";
            } else {
                output.success = true;
            }
        } else if (input.actionType === TCI_UPDATE_ACTION_TYPE.APP_DESCRIPTION) {
            if (input.application.description.length > 70) {
                output.msg = "Up to 70 characters are allowed";
                return Observable.of(output);
            } else {
                output.success = true;
            }
        } else if (input.actionType === TCI_UPDATE_ACTION_TYPE.UPLOAD_EXISTING) {
            output.success = true;
        } else if (input.actionType === TCI_UPDATE_ACTION_TYPE.UPDATE_TAGS) {
            output.success = true;
        }

        return output.success ? this.requestAppUpdate(input) : Observable.of(output);
    }

    /**
     * Do the HTTP update request
     * It updates "isWorking" variable before the call is sent and at response or error
     * @param input
     */
    protected requestAppUpdate(input: ITCIApplicationUpdateRequest): Observable<ITCIApplicationUpdateResponse> {
        this.isWorking = true;
        return this.http.put(`${SERVICE_URLS.APPS}/${input.appId}`, input)
            .map(res => res.json() as ITCIApplicationUpdateResponse)
            .catch(err => {
                console.error(input.tenantType.toString() + " App Update Error:", err.json());
                const output = this.getGenericResponse(input);
                output.msg = err.json().msg || "The application was not updated";
                return Observable.of(output);
            })
            .do(() => this.isWorking = false);
    }
}
